/* eslint-disable @typescript-eslint/no-deprecated */

import { Prism, Highlight, type RenderProps } from 'prism-react-renderer';
import { useEffect, useState } from 'react';

import { prismDqlLanguage } from './prism-dql-language.js';
import { isDev } from '../../core/utils/is-node-environment.js';
import { DTPrismTheme } from '../../core/utils/prism-theme.js';

// Ensure Prism is globally available
(typeof global !== 'undefined' ? global : window).Prism = Prism;

Prism.languages.dql = prismDqlLanguage;

/**
 * @public
 */
export type HighlightLanguage =
  | 'markup'
  | 'bash'
  | 'clike'
  | 'c'
  | 'cpp'
  | 'css'
  | 'javascript'
  | 'jsx'
  | 'coffeescript'
  | 'actionscript'
  | 'go'
  | 'graphql'
  | 'json'
  | 'markdown'
  | 'objectivec'
  | 'python'
  | 'reason'
  | 'sql'
  | 'tsx'
  | 'typescript'
  | 'yaml'
  | 'dql'
  | 'log';

interface HighlightExtendedProps {
  language?: HighlightLanguage;
  code: string;
  children: (props: RenderProps) => JSX.Element;
}

/**
 * Extends the `Highlight` component from the Prism library.
 * This component can be used across Strato components to handle loading additional and custom languages.
 * @internal
 */
export const HighlightExtended = (props: HighlightExtendedProps) => {
  const { language, code, children } = props;
  const [additionalLangLoaded, setAdditionalLangLoaded] = useState({
    bash: Prism.languages.bash !== undefined,
    log: Prism.languages.log !== undefined,
  });

  // This useEffect hook dynamically loads the additional Prism languages that are not included in the 'prism-react-renderer' basic set of languages.
  // With that it ensures that the necessary syntax highlighting for additional languages is implemented when the Prism instance is available.
  // The language prop accepts a limited set of languages that doesn't include `bash` and `log`, for which highlighting should be supported.
  // Therefore, they are imported from Prism.js.
  useEffect(() => {
    if (language === 'bash' && !additionalLangLoaded.bash) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment, @typescript-eslint/prefer-ts-expect-error
      // @ts-ignore
      import('prismjs/components/prism-bash.js')
        .then(() => {
          setAdditionalLangLoaded((prev) => ({ ...prev, bash: true }));
        })
        .catch((error) => {
          if (isDev()) {
            // eslint-disable-next-line no-console
            console.warn('Failed to load language:', error);
          }
        });
    }

    if (language === 'log' && !additionalLangLoaded.log) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment, @typescript-eslint/prefer-ts-expect-error
      // @ts-ignore
      import('prismjs/components/prism-log.js')
        .then(() => {
          setAdditionalLangLoaded((prev) => ({ ...prev, log: true }));
        })
        .catch((error) => {
          if (isDev()) {
            // eslint-disable-next-line no-console
            console.warn('Failed to load language:', error);
          }
        });
    }
  }, [language, additionalLangLoaded]);

  return (
    <Highlight
      language={language ?? 'plain'}
      code={code}
      theme={language === 'log' ? DTPrismTheme.Log : DTPrismTheme.Default}
      // @ts-expect-error language prop accepts limited set of languages that don't include `log`
      Prism={Prism}
    >
      {({ tokens, getLineProps, getTokenProps, className, style }) => (
        <>
          {children({
            tokens,
            getLineProps,
            getTokenProps,
            className,
            style,
          })}
        </>
      )}
    </Highlight>
  );
};
